import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '../../../core/network/api';
import { Gate } from '../../../global/contexts/auth/auth';
import { useClient } from '../contexts/clients';
import { useClientStats } from '../../stats/contexts/stats';
import ClientUserForm from '../forms/client-user';
import { StatsBlocks, StatsFilters } from '../../stats/layouts/entity-stats';
import { statsPaths } from '../../stats/queries/paths';
import { useGeneratePdf } from '../../reports/contexts/reports';
import { downloadPdf } from '../../reports/utils/shell';
import { buildReproductiveReport } from '../../kpis/utils/reproductive-report';

import '../../stats/layouts/styles/entity-stats.css';
import '../styles/stats.css';

const todayStr = () => new Date().toLocaleDateString('en-CA');
const yearStart = () => `${new Date().getFullYear()}-01-01`;

// Estadísticas de un cliente diferenciando su NATURALEZA (0045/0049 · admin-stats.md):
// el DTO combinado trae `donors_kpi` (donadoras co-poseídas = COWHOUSING) y `recipients_kpi`
// (receptoras / tag de parcería = PARCERÍA). No hay campo `type` (roles = Capa 4 futura):
// la naturaleza se DERIVA de qué bloques tienen conteo > 0, y se muestran los KPI que
// correspondan (cowhousing, parcería o ambas). Read-only, kpis:read.
export default function ClientStats({ clientId }) {
    const [range, setRange] = useState({ from: yearStart(), to: todayStr() });
    const [userForm, setUserForm] = useState(false);
    const { data: client } = useClient(clientId);
    const { data, isLoading, isFetching, error } = useClientStats(clientId, range);

    const donors = data?.donors_kpi;
    const recipients = data?.recipients_kpi;
    const hasCow = (donors?.count || 0) > 0;
    const hasParc = (recipients?.count || 0) > 0;
    const name = client?.name || data?.entity?.name || `#${clientId}`;

    // Informe reproductivo (bloque `report` del mismo endpoint, ?view=report) → PDF.
    const qc = useQueryClient();
    const { mutateAsync: generatePdf } = useGeneratePdf();
    const [pdfBusy, setPdfBusy] = useState(false);
    const onPdf = async () => {
        if (pdfBusy) return;
        setPdfBusy(true);
        try {
            const params = { view: 'report' };
            if (range.from) params.from = range.from;
            if (range.to) params.to = range.to;
            const reportData = await qc.fetchQuery({
                queryKey: ['client-report', clientId, range.from || null, range.to || null],
                queryFn: ({ signal }) => api.get(statsPaths.client(clientId), { params, signal }).then((r) => r.data),
                staleTime: 60_000,
            });
            const { html, filename, options } = buildReproductiveReport(reportData);
            const blob = await generatePdf({ html, filename, options });
            downloadPdf(blob, filename);
            toast.success('Informe generado');
        } catch (err) {
            if (err?.status !== 403) toast.error(err?.message || 'No se pudo generar el informe');
        } finally {
            setPdfBusy(false);
        }
    };

    return (
        <div className={`estats cstats ${isFetching ? 'estats--busy' : ''}`}>
            <header className="estats__header">
                <Link to={`/admin/clients/${clientId}`} className="estats__back">Volver al cliente</Link>
                <h1 className="estats__title">Cliente · {name}</h1>
                <p className="estats__subtitle">Indicadores reproductivos por naturaleza del cliente.</p>
                {(hasCow || hasParc) && (
                    <div className="cstats__natures">
                        {hasCow && <span className="cstats__nature cstats__nature--cowhousing">Cowhousing</span>}
                        {hasParc && <span className="cstats__nature cstats__nature--parceria">Parcería</span>}
                    </div>
                )}
            </header>

            <div className="cstats__toolbar">
                <StatsFilters range={range} onRange={(p) => setRange((r) => ({ ...r, ...p }))} busy={isFetching} />
                {client && (
                    <Gate domain="clients" action="write">
                        <button type="button" className="cstats__pdf" onClick={() => setUserForm(true)}>Usuario y acceso</button>
                    </Gate>
                )}
                <button type="button" className="cstats__pdf" onClick={onPdf} disabled={pdfBusy}>
                    {pdfBusy ? 'Generando…' : 'Descargar informe'}
                </button>
            </div>

            {error && (
                <div className="estats__error">
                    {error.status === 403 ? 'No tenés permiso para ver las estadísticas.' : (error.message || 'No se pudieron cargar las estadísticas.')}
                </div>
            )}

            {isLoading && !data ? (
                <div className="estats__state">Cargando estadísticas…</div>
            ) : (!hasCow && !hasParc) ? (
                <div className="estats__state">Este cliente no tiene donadoras (cowhousing) ni receptoras (parcería) con actividad en el período.</div>
            ) : (
                <>
                    {hasCow && (
                        <section className="cstats__section">
                            <div className="cstats__section-head">
                                <span className="cstats__badge cstats__badge--cowhousing">Cowhousing</span>
                                <h2 className="cstats__section-title">Donadoras</h2>
                                <span className="cstats__section-meta">
                                    {donors.count} donadora{donors.count === 1 ? '' : 's'} co-poseída{donors.count === 1 ? '' : 's'}
                                    {donors.avg_percent != null ? ` · ${donors.avg_percent}% copropiedad prom.` : ''}
                                </span>
                            </div>
                            <StatsBlocks data={donors} />
                        </section>
                    )}

                    {hasParc && (
                        <section className="cstats__section">
                            <div className="cstats__section-head">
                                <span className="cstats__badge cstats__badge--parceria">Parcería</span>
                                <h2 className="cstats__section-title">Receptoras</h2>
                                <span className="cstats__section-meta">{recipients.count} receptora{recipients.count === 1 ? '' : 's'}</span>
                            </div>
                            <StatsBlocks data={recipients} />
                        </section>
                    )}
                </>
            )}

            {userForm && client && <ClientUserForm client={client} onClose={() => setUserForm(false)} />}
        </div>
    );
}
